import { useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/authStore";
import { ROUTES } from "@/lib/constants/routes";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import authApi from "@/features/auth/api/authApi";
import { ThemeToggle } from "./ThemeToggle";
import LanguageSelector from "./LanguageSelector";
import Logo from "../Logo";

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const { user, isAuthenticated, clearAuth } = useAuthStore();
    const navigate = useNavigate();

    const navLinks = [
        { name: t('landing.nav.features'), href: "/#features" },
        { name: t('landing.nav.howItWorks'), href: "/#how-it-works" },
        { name: t('landing.nav.testimonials'), href: "/#testimonials" },
        { name: t('landing.nav.pricing'), href: ROUTES.PRICING },
    ];

    const handleLogout = async () => {
        try {
            await authApi.logout();
            clearAuth();
            navigate(`${ROUTES.REGISTER}?mode=login`);
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <Logo />

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                className="text-muted-foreground hover:text-african-accent transition-colors duration-200 font-medium"
                            >
                                {link.name}
                            </a>
                        ))}
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden lg:flex items-center gap-3">
                        {/* Language Selector */}
                        <LanguageSelector />

                        <ThemeToggle />

                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                                        <Avatar className="h-9 w-9 border border-border">
                                            <AvatarImage src={user?.avatar} alt={user?.name} />
                                            <AvatarFallback className="bg-african-accent/10 text-african-accent">
                                                {user?.name?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56 mt-2" align="end">
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="flex flex-col space-y-1">
                                            <p className="text-sm font-medium leading-none">{user?.name}</p>
                                            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to={ROUTES.DASHBOARD} className="flex items-center cursor-pointer">
                                            <LayoutDashboard className="mr-2 h-4 w-4" />
                                            <span>{t('header.dashboard')}</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to={ROUTES.SETTINGS} className="flex items-center cursor-pointer">
                                            <User className="mr-2 h-4 w-4" />
                                            <span>{t('sidebar.settings')}</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        <span>{t('header.logout')}</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button asChild variant="ghost" className="hover:text-african-accent hover:bg-transparent">
                                    <Link to={ROUTES.LOGIN}>
                                        {t('landing.nav.login')}
                                    </Link>
                                </Button>
                                <Button asChild className="bg-african-accent hover:bg-african-accent/90 text-white shadow-African h-11">
                                    <Link to={ROUTES.REGISTER}>
                                        {t('landing.nav.register')}
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="flex items-center gap-2 lg:hidden">
                        <ThemeToggle />
                        <button
                            className="p-2 text-foreground"
                            onClick={() => setIsOpen(!isOpen)}
                            aria-label="Toggle menu"
                        >
                            {isOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isOpen && (
                    <div className="lg:hidden p-4 border-t border-border bg-background animate-in slide-in-from-top duration-300">
                        <div className="flex flex-col gap-5">
                            {navLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    className="text-lg font-medium text-muted-foreground hover:text-african-accent transition-colors"
                                    onClick={() => setIsOpen(false)}
                                >
                                    {link.name}
                                </a>
                            ))}

                            {/* <div className="flex items-center justify-between py-2 border-t border-border pt-5"> */}

                            <LanguageSelector />

                            {/* <span className="text-sm font-medium text-muted-foreground">{t('common.language')}</span>
                                <div className="flex gap-4">
                                    {supportedLanguages.map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => i18n.changeLanguage(lang)}
                                            className={`text-sm font-bold ${i18n.language === lang ? 'text-african-accent' : 'text-muted-foreground'}`}
                                        >
                                            {lang.toUpperCase()}
                                        </button>
                                    ))}
                                </div> */}
                            {/* </div> */}

                            <div className="flex flex-col gap-3 pt-2">
                                {isAuthenticated ? (
                                    <>
                                        <Button asChild className="w-full bg-african-accent">
                                            <Link to={ROUTES.DASHBOARD} onClick={() => setIsOpen(false)}>
                                                {t('header.dashboard')}
                                            </Link>
                                        </Button>
                                        <Button variant="outline" onClick={handleLogout} className="w-full">
                                            {t('header.logout')}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button asChild variant="ghost" className="w-full border border-border">
                                            <Link to={ROUTES.LOGIN} onClick={() => setIsOpen(false)}>
                                                {t('landing.nav.login')}
                                            </Link>
                                        </Button>
                                        <Button asChild className="w-full bg-african-accent text-white h-11">
                                            <Link to={ROUTES.REGISTER} onClick={() => setIsOpen(false)}>
                                                {t('landing.nav.register')}
                                            </Link>
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
